/**
 * An assistant message, rendered.
 *
 * The agent answers in markdown — headings, lists, tables and, most of all,
 * fenced code, because a large share of what it returns is Revit API C#. Shown
 * as plain text, a reply reads as `**142 walls**` and a code block loses its
 * shape entirely. `bimgo-ai-web` renders the same content with `react-markdown`
 * + `remark-gfm`, and those are the two packages added here so both clients
 * parse the same dialect (GFM: tables, strikethrough, task lists).
 *
 * Two deliberate departures from letting the library do whatever it likes:
 *
 *   - **Links do not navigate this window.** A renderer that follows a link
 *     leaves the app — there is no back button in a desktop shell, and the
 *     model's links come from a language model, not from us. They go to the
 *     system browser through the bridge instead, and are inert when there is no
 *     bridge (a dev build in a browser).
 *   - **No raw HTML.** `react-markdown` does not enable `rehype-raw` by default
 *     and this does not add it; model output is untrusted text, and the only
 *     reason to parse HTML out of it would be to render it.
 *
 * With `mentions` set, `[[mention:...]]` markers in the text become chips
 * (`rehypeMentions`, item BI) — the assistant quotes a mention back the way
 * the user wrote it, and the chip is how the user recognises it.
 */
import { useMemo } from "react";
import ReactMarkdown, { type Options } from "react-markdown";
import remarkGfm from "remark-gfm";
import { MENTION_PATTERN_SOURCE } from "../../lib/mentionUtils";
import { rehypeMentions, type ExtractabilityLookup } from "../../lib/rehypeMentions";

export function Markdown({ text, mentions }: { text: string; mentions?: ExtractabilityLookup | null }) {
  const rehypePlugins = useMemo<NonNullable<Options["rehypePlugins"]>>(
    () => (mentions ? [[rehypeMentions, MENTION_PATTERN_SOURCE, mentions]] : []),
    [mentions],
  );
  return (
    <div className="md">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={rehypePlugins}
        components={{
          a: ({ href, children }) => (
            <a
              href={href}
              onClick={(e) => {
                e.preventDefault();
                if (href) void window.bimgo?.shell?.openExternal(href);
              }}
            >
              {children}
            </a>
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}
